import { Fragment, useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../api";

/**
 * Family Tax Headroom Tracker (superadmin-only): every family member's
 * aggregate FYTD income across every firm they're a proprietor/partner/
 * director of — synced from FVP every 5 minutes (jobs/familyIncomeSync.ts) —
 * run through Money's dual-regime engine, so it's obvious at a glance who's
 * approaching the ₹12L new-regime rebate ceiling for the FY.
 */

interface FirmContribution {
  firmId: string;
  firmName: string;
  gstin: string | null;
  entityType: string;
  role: string;
  sharePercentage: number | null;
  remunerationFytd: number;
  remunerationExcess: number;
  profitShareFytd: number;
}

interface PersonRow {
  pan: string;
  name: string;
  category: string;
  totalIncome: number;
  firms: FirmContribution[];
  dataQualityWarnings: string[];
  syncedAt: string;
  oldRegimeTax: number;
  newRegimeTax: number;
  betterRegime: "old" | "new";
  newRegimeHeadroomTo12L: number;
}

interface DashboardData {
  fy: string;
  ay: string;
  ayIsFallback: boolean;
  count: number;
  people: PersonRow[];
}

interface SaleMarginRow {
  transactionId: string;
  voucherNo: string | null;
  voucherDate: string;
  partyName: string | null;
  saleAmount: number;
  linkedCost: number | null;
  margin: number | null;
  marginPct: number | null;
  isLinked: boolean;
}

interface SaleMarginSummary {
  firmId: string;
  fy: string;
  saleCount: number;
  linkedCount: number;
  unlinkedCount: number;
  totalSaleAmount: number;
  totalLinkedCost: number;
  totalMargin: number;
  unlinkedSaleAmount: number;
  sales: SaleMarginRow[];
}

interface SaleMarginsResponse {
  fy: string;
  pan: string;
  firms: SaleMarginSummary[];
}

function inr(x: number | string): string {
  return Number(x).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function currentFy(): string {
  const now = new Date();
  const startYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return `${startYear}-${String((startYear + 1) % 100).padStart(2, "0")}`;
}

function headroomCls(headroom: number, totalIncome: number): string {
  if (totalIncome <= 0) return "net-zero";
  const usedPct = (totalIncome / 1_200_000) * 100;
  if (usedPct >= 100) return "net-neg";
  if (usedPct >= 85) return "net-pos"; // reuses the amber-ish "warn" treatment below via inline style
  return "net-zero";
}

export function FamilyTax() {
  const [fy, setFy] = useState(currentFy());
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [marginsOpenFor, setMarginsOpenFor] = useState<string | null>(null);
  const [marginsData, setMarginsData] = useState<SaleMarginsResponse | null>(null);
  const [marginsLoading, setMarginsLoading] = useState(false);
  const [marginsError, setMarginsError] = useState<string | null>(null);

  const fetchDashboard = useCallback(async (fyValue: string) => {
    setLoading(true);
    setError(null);
    try {
      const d = await api.get<DashboardData>(`/api/family-tax/dashboard?fy=${encodeURIComponent(fyValue)}`);
      setData(d);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load family income.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard(fy);
  }, [fy, fetchDashboard]);

  const openMargins = async (pan: string) => {
    if (marginsOpenFor === pan) {
      setMarginsOpenFor(null);
      return;
    }
    setMarginsOpenFor(pan);
    setMarginsData(null);
    setMarginsError(null);
    setMarginsLoading(true);
    try {
      const d = await api.get<SaleMarginsResponse>(`/api/family-tax/sale-margins?pan=${encodeURIComponent(pan)}&fy=${encodeURIComponent(fy)}`);
      setMarginsData(d);
    } catch (err) {
      setMarginsError(err instanceof ApiError ? err.message : "Failed to load sale margins.");
    } finally {
      setMarginsLoading(false);
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    setError(null);
    try {
      await api.post("/api/family-tax/sync-now", {});
      await fetchDashboard(fy);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Sync failed — is FVP_BASE_URL/MONEY_TAX_TOKEN configured?");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="recon-screen">
      <h1>Family Tax Headroom Tracker</h1>
      <p className="muted">
        Aggregate FYTD income for every family member across every firm they're a proprietor/partner/director
        of — target: keep each person ≤ ₹12,00,000 to stay fully rebated under the new regime.
      </p>

      <div className="recon-controls">
        <label>
          FY:{" "}
          <select value={fy} onChange={(e) => setFy(e.target.value)}>
            {[currentFy(), `${Number(currentFy().slice(0, 4)) - 1}-${currentFy().slice(0, 2)}`].map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </label>{" "}
        <button onClick={() => fetchDashboard(fy)} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </button>{" "}
        <button onClick={syncNow} disabled={syncing}>
          {syncing ? "Syncing…" : "Sync now from FVP"}
        </button>
        {data && (
          <span className="muted" style={{ marginLeft: "0.75rem" }}>
            AY {data.ay}
            {data.ayIsFallback ? " (fallback — this AY's slabs aren't loaded yet, showing latest available)" : ""}
          </span>
        )}
      </div>

      {error && <p className="warn">{error}</p>}

      {data && (
        <div className="recon-table-wrap">
          <table className="recon-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>PAN</th>
                <th>Total income (FYTD)</th>
                <th>Old regime tax</th>
                <th>New regime tax</th>
                <th>Better regime</th>
                <th>Headroom to ₹12L</th>
                <th>Synced</th>
              </tr>
            </thead>
            <tbody>
              {data.people.map((p) => (
                <Fragment key={p.pan}>
                  <tr
                    className={p.dataQualityWarnings.length > 0 ? "verified" : undefined}
                    onClick={() => setExpanded(expanded === p.pan ? null : p.pan)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>{p.name}</td>
                    <td className="muted">{p.pan}</td>
                    <td>₹{inr(p.totalIncome)}</td>
                    <td>₹{inr(p.oldRegimeTax)}</td>
                    <td>₹{inr(p.newRegimeTax)}</td>
                    <td>{p.betterRegime}</td>
                    <td className={headroomCls(p.newRegimeHeadroomTo12L, p.totalIncome)}>
                      ₹{inr(p.newRegimeHeadroomTo12L)}
                    </td>
                    <td className="muted">{new Date(p.syncedAt).toLocaleString("en-IN")}</td>
                  </tr>
                  {expanded === p.pan && (
                    <tr>
                      <td colSpan={8}>
                        {p.dataQualityWarnings.length > 0 && (
                          <p className="warn">{p.dataQualityWarnings.join(" ")}</p>
                        )}
                        <table className="recon-table">
                          <thead>
                            <tr>
                              <th>Firm</th>
                              <th>Role</th>
                              <th>Share %</th>
                              <th>Remuneration FYTD</th>
                              <th>Remuneration excess</th>
                              <th>Profit share FYTD</th>
                            </tr>
                          </thead>
                          <tbody>
                            {p.firms.map((f) => (
                              <tr key={f.firmId}>
                                <td>{f.firmName}</td>
                                <td>{f.role}</td>
                                <td>{f.sharePercentage != null ? `${f.sharePercentage}%` : "—"}</td>
                                <td>₹{inr(f.remunerationFytd)}</td>
                                <td className={f.remunerationExcess > 0 ? "net-neg" : undefined}>
                                  {f.remunerationExcess > 0 ? `₹${inr(f.remunerationExcess)}` : "—"}
                                </td>
                                <td>₹{inr(f.profitShareFytd)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        <div style={{ marginTop: "0.75rem" }}>
                          <button onClick={() => openMargins(p.pan)}>
                            {marginsOpenFor === p.pan ? "Hide sale margins" : "Show margin per sale"}
                          </button>
                        </div>

                        {marginsOpenFor === p.pan && (
                          <div style={{ marginTop: "0.5rem" }}>
                            {marginsLoading && <p className="muted">Loading sale margins…</p>}
                            {marginsError && <p className="warn">{marginsError}</p>}
                            {marginsData && marginsData.firms.map((fm) => {
                              const firmName = p.firms.find((f) => f.firmId === fm.firmId)?.firmName || fm.firmId;
                              return (
                                <div key={fm.firmId} style={{ marginBottom: "1rem" }}>
                                  <p style={{ fontWeight: 600, margin: "0.5rem 0 0.25rem" }}>{firmName}</p>
                                  <p className="recon-summary" style={{ margin: "0 0 0.5rem" }}>
                                    <span>Sales: <b>{fm.saleCount}</b></span>
                                    <span>Linked: <b>{fm.linkedCount}</b></span>
                                    <span className={fm.unlinkedCount > 0 ? "net-neg" : undefined}>
                                      Unlinked: <b>{fm.unlinkedCount}</b>
                                      {fm.unlinkedCount > 0 ? ` (₹${inr(fm.unlinkedSaleAmount)} — not counted in margin below)` : ""}
                                    </span>
                                    <span>Total margin (linked only): <b>₹{inr(fm.totalMargin)}</b></span>
                                  </p>
                                  {fm.sales.length === 0 ? (
                                    <p className="muted">No sales in this FY.</p>
                                  ) : (
                                    <div className="recon-table-wrap">
                                      <table className="recon-table">
                                        <thead>
                                          <tr>
                                            <th>Date</th>
                                            <th>Voucher</th>
                                            <th>Party</th>
                                            <th>Sale Amount</th>
                                            <th>Linked Cost</th>
                                            <th>Margin</th>
                                            <th>Margin %</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {fm.sales.map((s) => (
                                            <tr key={s.transactionId} className={!s.isLinked ? undefined : "verified"}>
                                              <td>{s.voucherDate}</td>
                                              <td className="muted">{s.voucherNo || "—"}</td>
                                              <td>{s.partyName || "—"}</td>
                                              <td>₹{inr(s.saleAmount)}</td>
                                              <td>{s.isLinked ? `₹${inr(s.linkedCost as number)}` : <span className="warn">Unlinked</span>}</td>
                                              <td className={s.isLinked && (s.margin as number) < 0 ? "net-neg" : undefined}>
                                                {s.isLinked ? `₹${inr(s.margin as number)}` : "—"}
                                              </td>
                                              <td>{s.isLinked ? `${s.marginPct}%` : "—"}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
          {data.count === 0 && <p className="muted">No synced income yet — try "Sync now from FVP".</p>}
        </div>
      )}
    </div>
  );
}
